import useFetch from "hooks/useFetch";
import { useEffect, useRef, useState } from "react";
import { useNotificationsStore } from "store/notificationsStore";
import { useTranslation } from "react-i18next";
import { getToken } from "utils/getToken";
import { formatDateDDMMYYYY } from "utils/formateDate";
import { Avatar, Box, IconButton, List, ListItem, ListItemAvatar, Typography } from "@mui/material";
import { INotification, INotificationRenderData } from "interfaces/Notifications";
import CardGiftcardIcon from "@mui/icons-material/CardGiftcard";
import Warning from "assets/icons/Warning"; // warning табличка
import NotificationUpArrow from "assets/icons/NotificationUpArrow";
// import NotificationDownArrow from "assets/icons/NotificationDownArrow"; // стрелка вниз
// import NotificationCalendar from "assets/icons/NotificationCalendar"; // календарь
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import duckRegisterIcon from "assets/notoficationRegister.png";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CircleIcon from "@mui/icons-material/Circle";
import LayersClearIcon from "@mui/icons-material/LayersClear";
import { BootstrapTooltip } from "components/PWATabs/mainTab/CommentsTable";
import { toast } from "react-toastify";
import Preloader from "components/GlobalPreloader/Preloader";

interface NotificationsDropdownProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}

type Locale = "ua" | "ru" | "en";

const getNotificationRenderData = (notification: INotification): INotificationRenderData => {
  switch (notification.type) {
    case "register":
      return {
        icon: duckRegisterIcon,
        avatarBackground: "#f6f7fb",
        title: "notificationRegister",
      };
    case "deposit":
      return {
        icon: <NotificationUpArrow />,
        avatarBackground: "#dfffea",
        title: "notificationDeposit",
      };
    case "promocode":
      return {
        icon: <CardGiftcardIcon sx={{ width: "24px", height: "24px", color: "#8C57FF" }} />,
        avatarBackground: "#8C57FF29",
        title: "notificationPromocode",
      };
    case "low-balance":
      return {
        icon: <AccountBalanceWalletIcon sx={{ width: "24px", height: "24px", color: "#f4701b" }} />,
        avatarBackground: "#FCEFE9",
        title: "notificationLowBalance",
      };
    case "domain-frozen":
      return {
        icon: <Warning />,
        avatarBackground: "#ffeef3",
        title: "notificationDomainFrozen",
      };
    case "domain-delete":
      return {
        icon: <DeleteOutlineIcon sx={{ width: "24px", height: "24px", color: "#F04438" }} />,
        avatarBackground: "#ffeef3",
        title: "notificationDomainDeleted",
      };
    default:
      return {
        icon: <NotificationsActiveIcon sx={{ width: "24px", height: "24px", color: "#16B1FF" }} />,
        avatarBackground: "rgba(22, 177, 255, 0.16)",
        title: "notificationDefault",
      };
  }
};

const NotificationsDropdown = ({ isOpen, setIsOpen }: NotificationsDropdownProps) => {
  const token = getToken();
  const { t, i18n } = useTranslation();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const currentLanguage = (i18n.language as Locale) ?? "en";
  const { personalNotificationsArr, saveNotifications } = useNotificationsStore();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const unreadNotificationsIds = personalNotificationsArr
    .filter((notification) => !notification.isRead)
    .map((notification) => notification._id);

  const { triggerFetch: updateNitificationsReadStatus } = useFetch({
    url: "notification/read-status",
    method: "PATCH",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  const { triggerFetch: deleteNotifications } = useFetch({
    url: "notification",
    method: "DELETE",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  const handleUpdateNotifications = async () => {
    if (!unreadNotificationsIds || !unreadNotificationsIds.length) {
      return;
    }
    const res = await updateNitificationsReadStatus({ unreadNotificationsIds });
    if (res.success) {
      saveNotifications(res.data.items.reverse());
    }
  };

  const handleDeleteNotifications = async () => {
    try {
      if (!personalNotificationsArr.length) {
        return;
      }

      setIsLoading(true);
      const res = await deleteNotifications();

      if (res?.success) {
        saveNotifications([]);
        toast.success(t("successNotificationsDelete"));
      }
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      toast.error(t("unsuccessNotificationsDelete"));
    }
  };

  useEffect(() => {
    if (!isOpen) return;

    return () => {
      void handleUpdateNotifications();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    const handleEscKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscKey);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscKey);
    };
  }, [setIsOpen]);

  return (
    <Box ref={dropdownRef} sx={{ position: "relative", height: "30px" }}>
      {isOpen && (
        <Box
          sx={{
            position: "absolute",
            top: {
              xs: "calc(100% + 15px)",
              lg: "calc(100% + 22px)",
            },
            right: 0,
            maxWidth: { xs: "280px", md: "400px" },
            width: { xs: "100dvw", md: "400px" },
            backgroundColor: "#fff",
            border: "1px solid #eeeeee",
            borderRadius: "0px 0px 8px 8px",
            boxShadow: "0 0 4px 0 rgba(165, 163, 174, 0.3);",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              height: "56px",
              borderBottom: "1px solid #eee",
            }}
          >
            <Typography
              component="h2"
              sx={{ marginLeft: "24px", fontSize: "18px", fontWeight: 500, color: "#2e263d", lineHeight: 1.17 }}
            >
              {t("notifications")}
            </Typography>
            <BootstrapTooltip title={t("clearAllNotifications")} placement="top">
              <IconButton
                sx={{ marginRight: "12px" }}
                aria-label="delete-notifications"
                onClick={handleDeleteNotifications}
              >
                <LayersClearIcon
                  sx={{
                    width: "20px",
                    height: "20px",
                  }}
                />
              </IconButton>
            </BootstrapTooltip>
          </Box>
          <List sx={{ position: "relative", maxHeight: "510px", overflowY: isLoading ? "hidden" : "auto" }}>
            {!personalNotificationsArr.length ? (
              <ListItem sx={{ minHeight: "100px", justifyContent: "center" }}>
                <Typography
                  component="p"
                  sx={{
                    fontSize: "14px",
                    fontWeight: 400,
                    color: "#6d6777",
                    lineHeight: 1.43,
                    textAlign: "center",
                  }}
                >
                  {t("emptyNotificationsList")}
                </Typography>
              </ListItem>
            ) : (
              personalNotificationsArr.map((notification) => {
                const { icon, avatarBackground, title } = getNotificationRenderData(notification);
                const { text, createdAt, isRead } = notification;
                const notificationDate = formatDateDDMMYYYY(createdAt);
                return (
                  <ListItem key={notification._id}>
                    <ListItemAvatar sx={{ marginBottom: "auto" }}>
                      <Avatar sx={{ backgroundColor: avatarBackground }}>
                        {typeof icon === "string" ? (
                          <img src={icon} alt="register notification icon" style={{ width: 32, height: 32 }} />
                        ) : (
                          icon
                        )}
                      </Avatar>
                    </ListItemAvatar>
                    <Box sx={{ width: "100%" }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          marginBottom: "8px",
                        }}
                      >
                        <Typography
                          component="h4"
                          sx={{
                            fontSize: "14px",
                            fontWeight: 500,
                            color: "#2e263d",
                            lineHeight: 1.5,
                          }}
                        >
                          {t(title)}
                        </Typography>
                        {!isRead && <CircleIcon sx={{ width: "8px", height: "8px", color: "#F4701B" }} />}
                      </Box>
                      <Typography
                        component="p"
                        sx={{
                          marginBottom: "14px",
                          fontSize: "14px",
                          fontWeight: 400,
                          color: "#6d6777",
                          lineHeight: 1.43,
                        }}
                      >
                        {text[currentLanguage]}
                      </Typography>
                      <Typography
                        component="span"
                        sx={{
                          fontSize: "12px",
                          fontWeight: 400,
                          color: "#b4b1b9",
                          lineHeight: 1.75,
                        }}
                      >
                        {notificationDate}
                      </Typography>
                    </Box>
                  </ListItem>
                );
              })
            )}
            {isLoading && (
              <Preloader
                customStyles={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  margin: 0,
                  borderRadius: "0px",
                  backgroundColor: "rgba(255,255,255,0.7)",
                }}
              />
            )}
          </List>
        </Box>
      )}
    </Box>
  );
};

export default NotificationsDropdown;
